import React from 'react'
import ZoomVideo, { VideoQuality } from '@zoom/videosdk'

function invokeCallback(callback, ...args) {
  if (typeof callback === 'function') {
    callback(...args)
  }
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()
  return normalizedValue || fallbackValue
}

function buildZoomVideoQuality(videoQuality) {
  const height = videoQuality?.subscribeProfile?.height || videoQuality?.publishProfile?.height || 360

  if (height >= 1080) {
    return VideoQuality.Video_1080P
  }

  if (height >= 720) {
    return VideoQuality.Video_720P
  }

  if (height >= 360) {
    return VideoQuality.Video_360P
  }

  if (height >= 180) {
    return VideoQuality.Video_180P
  }

  return VideoQuality.Video_90P
}

function buildParticipantSnapshot({ client, stream, rtcProps }) {
  const currentUser = client?.getCurrentUserInfo?.() || null
  const currentUserId = currentUser?.userId
  const users = Array.isArray(client?.getAllUser?.()) ? client.getAllUser() : []
  const usersById = new Map()

  users.forEach((user) => {
    if (user?.userId !== undefined && user?.userId !== null) {
      usersById.set(String(user.userId), user)
    }
  })

  if (currentUser?.userId !== undefined && currentUser?.userId !== null) {
    usersById.set(String(currentUser.userId), currentUser)
  }

  return {
    videoQuality: buildZoomVideoQuality(rtcProps?.videoQuality),
    participants: Array.from(usersById.values()).map((user) => ({
      ...user,
      stream,
      isLocal: user?.userId === currentUserId,
      participantMetadata:
        user?.userKey ||
        user?.userIdentity ||
        (user?.userId === currentUserId ? rtcProps?.participantMetadata : null),
      videoQuality: buildZoomVideoQuality(rtcProps?.videoQuality),
    })),
  }
}

function useLatestRef(value) {
  const ref = React.useRef(value)
  ref.current = value
  return ref
}

export function ZoomRtcCompatibilityShell({
  rtcProps = {},
  callbacks = {},
  fallback = null,
  onProviderError,
  renderless = false,
  children = null,
}) {
  const callbacksRef = useLatestRef(callbacks)
  const onProviderErrorRef = useLatestRef(onProviderError)
  const rtcPropsRef = useLatestRef(rtcProps)
  const clientRef = React.useRef(null)
  const streamRef = React.useRef(null)
  const shareRenderElementRef = React.useRef(null)
  const [joinedVersion, setJoinedVersion] = React.useState(0)
  const joinedRef = React.useRef(false)
  const joiningKeyRef = React.useRef(null)
  const previousConnectionStateRef = React.useRef('idle')
  const previousRemoteUserIdsRef = React.useRef(new Set())
  const localAudioEnabledRef = React.useRef(null)
  const localVideoEnabledRef = React.useRef(null)
  const localScreenShareEnabledRef = React.useRef(false)

  const emitParticipantSnapshot = React.useCallback(() => {
    const client = clientRef.current
    const stream = streamRef.current
    if (!client || !stream) {
      return
    }

    invokeCallback(
      callbacksRef.current?.['participants-updated'],
      buildParticipantSnapshot({
        client,
        stream,
        rtcProps: rtcPropsRef.current,
      }),
    )
  }, [callbacksRef, rtcPropsRef])

  const emitConnectionState = React.useCallback(
    (nextState, reason = null) => {
      const previousState = previousConnectionStateRef.current
      previousConnectionStateRef.current = nextState
      invokeCallback(
        callbacksRef.current?.['connection-state-change'],
        nextState,
        previousState,
        reason,
      )
    },
    [callbacksRef],
  )

  React.useEffect(() => {
    const sessionName = normalizeOptionalString(rtcProps?.sessionName)
    const token = normalizeOptionalString(rtcProps?.token)
    const userName = normalizeOptionalString(rtcProps?.userName, 'AUMHUM participant')

    if (!sessionName || !token || !userName) {
      invokeCallback(
        onProviderErrorRef.current,
        new Error('Missing Zoom Video SDK session name, token, or username'),
      )
      return undefined
    }

    const joinKey = [
      sessionName,
      token,
      userName,
      rtcProps?.sessionPassword || '',
    ].join('::')

    if (joinedRef.current && joiningKeyRef.current === joinKey) {
      return undefined
    }

    joiningKeyRef.current = joinKey
    let cancelled = false
    const client = ZoomVideo.createClient()
    clientRef.current = client

    const handleUsersChanged = () => {
      if (!cancelled) {
        emitParticipantSnapshot()
      }
    }
    const handleUsersAdded = (users = []) => {
      const nextUserIds = new Set(previousRemoteUserIdsRef.current)
      ;(Array.isArray(users) ? users : [users]).forEach((user) => {
        if (user?.userId === undefined || user?.userId === null) {
          return
        }
        const userId = String(user.userId)
        if (!nextUserIds.has(userId)) {
          nextUserIds.add(userId)
          invokeCallback(callbacksRef.current?.onUserJoin, user)
        }
      })
      previousRemoteUserIdsRef.current = nextUserIds
      handleUsersChanged()
    }
    const handleUsersRemoved = (users = []) => {
      const nextUserIds = new Set(previousRemoteUserIdsRef.current)
      ;(Array.isArray(users) ? users : [users]).forEach((user) => {
        if (user?.userId === undefined || user?.userId === null) {
          return
        }
        nextUserIds.delete(String(user.userId))
        invokeCallback(callbacksRef.current?.onUserLeave, user)
      })
      previousRemoteUserIdsRef.current = nextUserIds
      handleUsersChanged()
    }
    const handleConnectionChange = (payload = {}) => {
      emitConnectionState(payload?.state || 'unknown', payload?.reason || null)
    }

    client.on('connection-change', handleConnectionChange)
    client.on('user-added', handleUsersAdded)
    client.on('user-updated', handleUsersChanged)
    client.on('user-removed', handleUsersRemoved)
    client.on('peer-video-state-change', handleUsersChanged)
    client.on('active-share-change', handleUsersChanged)
    client.on('peer-share-state-change', handleUsersChanged)
    client.on('current-audio-change', handleUsersChanged)

    emitConnectionState('joining', null)
    client
      .init(rtcProps?.language || 'en-US', rtcProps?.dependentAssets || 'Global', {
        patchJsMedia: true,
        leaveOnPageUnload: true,
        stayAwake: true,
        enforceMultipleVideos: true,
        ...(rtcProps?.webEndpoint ? { webEndpoint: rtcProps.webEndpoint } : {}),
      })
      .then(() =>
        client.join(
          sessionName,
          token,
          userName,
          rtcProps?.sessionPassword || '',
          rtcProps?.sessionIdleTimeoutMins,
        ),
      )
      .then(() => {
        if (cancelled) {
          return
        }

        joinedRef.current = true
        streamRef.current = client.getMediaStream()
        setJoinedVersion((version) => version + 1)
        emitConnectionState('joined', null)
        emitParticipantSnapshot()
      })
      .catch((error) => {
        console.error('[ZoomRtcCompatibilityShell] Join failed', error)
        joinedRef.current = false
        joiningKeyRef.current = null
        emitConnectionState('failed', error?.reason || error?.message || null)

        if (!cancelled) {
          invokeCallback(onProviderErrorRef.current, error)
        }
      })

    return () => {
      cancelled = true
      joinedRef.current = false
      localAudioEnabledRef.current = null
      localVideoEnabledRef.current = null
      localScreenShareEnabledRef.current = false
      streamRef.current = null
      previousRemoteUserIdsRef.current = new Set()
      client.leave(false).catch(() => {})
      ZoomVideo.destroyClient()
    }
  }, [
    callbacksRef,
    emitConnectionState,
    emitParticipantSnapshot,
    onProviderErrorRef,
    rtcPropsRef,
    rtcProps?.dependentAssets,
    rtcProps?.language,
    rtcProps?.sessionIdleTimeoutMins,
    rtcProps?.sessionName,
    rtcProps?.sessionPassword,
    rtcProps?.token,
    rtcProps?.userName,
    rtcProps?.webEndpoint,
  ])

  React.useEffect(() => {
    const stream = streamRef.current
    if (!joinedRef.current || !stream) {
      return
    }

    const enabled = rtcProps?.enableAudio !== false
    if (localAudioEnabledRef.current === enabled) {
      return
    }

    localAudioEnabledRef.current = enabled
    const task = enabled
      ? stream.startAudio({
          mute: false,
          microphoneId: rtcProps?.microphoneDeviceId,
          speakerId: rtcProps?.playbackDeviceId,
        }).then(() => stream.unmuteAudio())
      : stream.muteAudio()

    task
      .then(() => {
        invokeCallback(callbacksRef.current?.onAudioStateChange, enabled)
        emitParticipantSnapshot()
      })
      .catch((error) => {
        console.warn('[ZoomRtcCompatibilityShell] Audio state change failed', error)
      })
  }, [
    callbacksRef,
    emitParticipantSnapshot,
    joinedVersion,
    rtcProps?.enableAudio,
    rtcProps?.microphoneDeviceId,
    rtcProps?.playbackDeviceId,
  ])

  React.useEffect(() => {
    const stream = streamRef.current
    if (!joinedRef.current || !stream) {
      return
    }

    const enabled = rtcProps?.enableVideo !== false
    if (localVideoEnabledRef.current === enabled) {
      return
    }

    localVideoEnabledRef.current = enabled
    const task = enabled
      ? stream.startVideo({
          cameraId: rtcProps?.cameraDeviceId,
          hd: buildZoomVideoQuality(rtcProps?.videoQuality) >= VideoQuality.Video_720P,
          fullHd: buildZoomVideoQuality(rtcProps?.videoQuality) >= VideoQuality.Video_1080P,
        })
      : stream.stopVideo()

    task
      .then(() => {
        invokeCallback(callbacksRef.current?.onCameraStateChange, enabled)
        emitParticipantSnapshot()
      })
      .catch((error) => {
        console.warn('[ZoomRtcCompatibilityShell] Video state change failed', error)
      })
  }, [
    callbacksRef,
    emitParticipantSnapshot,
    joinedVersion,
    rtcProps?.cameraDeviceId,
    rtcProps?.enableVideo,
    rtcProps?.videoQuality,
  ])

  React.useEffect(() => {
    const stream = streamRef.current
    if (!joinedRef.current || !stream || !rtcProps?.cameraDeviceId) {
      return
    }

    stream.switchCamera(rtcProps.cameraDeviceId).catch(() => {})
  }, [rtcProps?.cameraDeviceId])

  React.useEffect(() => {
    const stream = streamRef.current
    if (!joinedRef.current || !stream || !rtcProps?.microphoneDeviceId) {
      return
    }

    stream.switchMicrophone(rtcProps.microphoneDeviceId).catch(() => {})
  }, [rtcProps?.microphoneDeviceId])

  React.useEffect(() => {
    const stream = streamRef.current
    if (!joinedRef.current || !stream) {
      return
    }

    const enabled = rtcProps?.enableScreensharing === true
    if (localScreenShareEnabledRef.current === enabled) {
      return
    }

    localScreenShareEnabledRef.current = enabled

    if (!enabled) {
      stream.stopShareScreen().catch(() => {})
      invokeCallback(callbacksRef.current?.['screen-share-state-changed'], false)
      emitParticipantSnapshot()
      return
    }

    const shareRenderElement = document.createElement('canvas')
    shareRenderElementRef.current = shareRenderElement
    stream
      .startShareScreen(shareRenderElement, {
        optimizedForSharedVideo: false,
        simultaneousShareView: true,
      })
      .then(() => {
        invokeCallback(callbacksRef.current?.['screen-share-state-changed'], true)
        emitParticipantSnapshot()
      })
      .catch((error) => {
        localScreenShareEnabledRef.current = false
        console.warn('[ZoomRtcCompatibilityShell] Screen share failed', error)
        invokeCallback(callbacksRef.current?.['screen-share-state-changed'], false)
      })
  }, [
    callbacksRef,
    emitParticipantSnapshot,
    joinedVersion,
    rtcProps?.enableScreensharing,
  ])

  if (renderless) {
    return children
  }

  return fallback
}

export default ZoomRtcCompatibilityShell
